import { useMemo, type FC } from "react";
import { type PlainAgentExecution } from "@/queries/agent-queries";
import { Link } from "@/components/flexkit/Link";
import { Text } from "@/components/podkit/typography/Text";
import { useMember } from "@/hooks/use-member";
import { timeAgo } from "@/format/time";
import { Timestamp } from "@bufbuild/protobuf";
import { Card } from "@/components/flexkit/Card";
import { AgentExecutionStatusIcon } from "@/components/ai/AgentExecutionStatusIcon";
import { AgentExecutionStatusLabel } from "@/components/ai/AgentExecutionStatusLabel";
import { getAgentExecutionUrl } from "@/routes/ai/ai-urls";
import { agentExecutionName } from "@/utils/agent-executions";

export const AgentExecutionCard: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    return (
        <Card className="flex justify-between gap-4" asChild>
            <Link
                to={getAgentExecutionUrl(agentExecution.id)}
                className="block hover:ring-2 hover:ring-content-orange focus-visible:ring-2 focus-visible:ring-content-orange"
            >
                <NameAndAuthor agentExecution={agentExecution} />
                <StartedAt agentExecution={agentExecution} />
                <Status agentExecution={agentExecution} />
            </Link>
        </Card>
    );
};

const NameAndAuthor: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const { member } = useMember(agentExecution.metadata?.creator?.id);
    const name = useMemo(() => agentExecutionName(agentExecution), [agentExecution]);
    return (
        <div className="flex w-1/3 max-w-[33%] grow flex-col gap-0.5">
            <Text className="min-w-0 truncate text-lg font-bold text-content-primary" title={name}>
                {name}
            </Text>
            <Text className="text-sm text-content-secondary">{member?.fullName}</Text>
        </div>
    );
};

const StartedAt: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const date = new Timestamp(agentExecution?.metadata?.createdAt).toDate();
    return (
        <div className="flex shrink flex-col gap-0.5">
            <Text className="text-base text-content-primary">Started</Text>
            <Text className="text-sm text-content-secondary">{timeAgo(date)}</Text>
        </div>
    );
};

const Status: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    return (
        <div className="flex w-32 shrink items-center justify-end gap-2">
            <AgentExecutionStatusLabel agentExecution={agentExecution} />
            <AgentExecutionStatusIcon agentExecution={agentExecution} />
        </div>
    );
};
